import { useState } from 'react';
import { Link } from 'react-router-dom';
import Seo from '@/components/base/Seo';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollTopButton from '@/components/feature/ScrollTopButton';
import { siteName, siteSlogan, siteDesc, games, hiddenStories, endingRoutes, testimonials, faqs, categories } from '@/mocks/siteData';

const homeSeo: Record<string, unknown>[] = [
  {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteName,
    slogan: siteSlogan,
    description: siteDesc,
  },
  {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: siteName,
    description: siteDesc,
  },
  {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: games.slice(0, 4).map((g, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: g.title,
      description: g.desc,
    })),
  },
];

function FaqSection() {
  const [openId, setOpenId] = useState<string | null>(faqs[0].id);
  return (
    <section id="faq" className="mx-auto max-w-3xl px-4 py-16 md:py-20 md:px-6">
      <div className="text-center">
        <p className="font-label text-sm font-bold tracking-widest text-accent-600">常见问题</p>
        <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">关于攻略与剧情，你想知道的</h2>
      </div>
      <div className="mt-10 space-y-3">
        {faqs.map((f) => {
          const open = openId === f.id;
          return (
            <div key={f.id} className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
              <button
                type="button"
                onClick={() => setOpenId(open ? null : f.id)}
                aria-expanded={open}
                className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
              >
                <span className="text-sm font-bold text-foreground-900">{f.q}</span>
                <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-accent-600 transition-transform ${open ? 'rotate-45' : ''}`}>
                  <i className="ri-add-line text-xl"></i>
                </span>
              </button>
              {open && (
                <div className="border-t border-background-200 px-5 py-4">
                  <p className="text-sm leading-relaxed text-foreground-600">{f.a}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function Hero() {
  return (
    <section className="relative flex min-h-[640px] w-full items-center justify-center overflow-hidden">
      <img
        src="https://readdy.ai/api/search-image?query=Stylized%20anime%20ink%20illustration%20of%20a%20lone%20ninja%20silhouette%20standing%20on%20a%20rooftop%20at%20dusk%20under%20a%20giant%20setting%20orange%20sun%2C%20swirling%20fire%20and%20ember%20particles%20drifting%20through%20the%20air%2C%20faint%20paper%20lanterns%20in%20the%20deep%20indigo%20to%20black%20night%20sky%2C%20warm%20amber%20and%20crimson%20palette%2C%20dramatic%20silhouette%20contrast%2C%20cinematic%20wide%20composition%2C%20painterly%20brushwork&width=1600&height=900&seq=hero-home-2026&orientation=landscape"
        alt="火影同人游戏攻略站首屏，忍者剪影与燃烧的查克拉之火"
        className="absolute inset-0 h-full w-full object-cover object-center"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/70 via-foreground-950/40 to-foreground-950/80"></div>
      <div className="relative z-10 mx-auto w-full max-w-4xl px-4 py-24 text-center md:px-6">
        <p className="font-label text-sm tracking-[0.3em] text-accent-300">火影同人游戏 · 剧情向RPG · 多结局攻略</p>
        <h1 className="mt-6 font-heading text-3xl font-bold leading-tight text-background-50 md:text-5xl">
          把每一条故事线<br className="md:hidden" />都玩到尽头
        </h1>
        <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-background-100/90 md:text-lg">
          面向剧情党的火影同人RPG攻略站，整理角色扮演体验、隐藏剧情触发条件与多结局走向，不漏关键情节，不错过任何真相。
        </p>
        <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <Link to="/games" className="whitespace-nowrap rounded-md bg-accent-500 px-7 py-3 text-sm font-bold text-foreground-950 transition-colors hover:bg-accent-400">
            浏览游戏库
          </Link>
          <a href="#hidden" className="whitespace-nowrap rounded-md border border-background-100/60 px-7 py-3 text-sm font-bold text-background-50 transition-colors hover:bg-background-100/10">
            查看隐藏剧情
          </a>
        </div>
        <div className="mt-12 grid grid-cols-3 gap-4 border-t border-background-100/20 pt-8 text-center">
          <div>
            <p className="font-heading text-2xl font-bold text-accent-300">{games.length}</p>
            <p className="mt-1 font-label text-xs text-background-100/80">收录游戏</p>
          </div>
          <div>
            <p className="font-heading text-2xl font-bold text-accent-300">{hiddenStories.length}+</p>
            <p className="mt-1 font-label text-xs text-background-100/80">隐藏剧情</p>
          </div>
          <div>
            <p className="font-heading text-2xl font-bold text-accent-300">{endingRoutes.length}</p>
            <p className="mt-1 font-label text-xs text-background-100/80">结局路线</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function CategoryStrip() {
  const items = [
    { icon: 'ri-book-open-line', label: '剧情向', desc: '以叙事与选择为核心的RPG' },
    { icon: 'ri-user-heart-line', label: '角色扮演', desc: '深度代入角色的成长旅程' },
    { icon: 'ri-git-branch-line', label: '多结局', desc: '信任值与羁绊决定走向' },
    { icon: 'ri-compass-3-line', label: '高自由', desc: '开放选择决定进程' },
  ];
  return (
    <section className="border-b border-background-200 bg-background-50">
      <div className="mx-auto grid max-w-6xl grid-cols-1 gap-4 px-4 py-12 sm:grid-cols-2 lg:grid-cols-4 md:px-6">
        {items.map((it) => (
          <Link
            key={it.label}
            to="/games"
            className="group flex items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-300"
          >
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-700 transition-colors group-hover:bg-primary-500 group-hover:text-background-50">
              <i className={`${it.icon} text-xl`}></i>
            </span>
            <span>
              <span className="block text-sm font-bold text-foreground-950">{it.label}</span>
              <span className="mt-1 block text-xs text-foreground-600">{it.desc}</span>
            </span>
          </Link>
        ))}
      </div>
    </section>
  );
}

function FeaturedGames() {
  const featured = games.slice(0, 4);
  return (
    <section className="mx-auto max-w-6xl px-4 py-16 md:py-20 md:px-6">
      <div className="flex items-end justify-between">
        <div>
          <p className="font-label text-sm font-bold tracking-widest text-accent-600">热门推荐</p>
          <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">剧情党必玩的火影同人RPG</h2>
        </div>
        <Link to="/games" className="hidden whitespace-nowrap items-center gap-1 text-sm font-bold text-primary-600 hover:text-primary-700 sm:flex">
          查看全部 <i className="ri-arrow-right-line"></i>
        </Link>
      </div>
      <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {featured.map((g) => (
          <article key={g.id} className="group overflow-hidden rounded-lg border border-background-200 bg-background-50">
            <div className="relative aspect-[3/4] overflow-hidden">
              <img src={g.cover} alt={`${g.title}游戏封面`} className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
              <span className="absolute left-3 top-3 rounded-md bg-foreground-950/70 px-2 py-1 font-label text-xs text-background-50 backdrop-blur">
                {g.category}
              </span>
            </div>
            <div className="p-4">
              <h3 className="font-heading text-base font-bold text-foreground-950">{g.title}</h3>
              <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-foreground-600">{g.tagline}</p>
              <div className="mt-3 flex items-center justify-between">
                <span className="font-label text-xs text-primary-600">
                  <i className="ri-star-fill mr-1 text-accent-500"></i>
                  {g.rating.toFixed(1)}
                </span>
                <span className="font-label text-xs text-foreground-500">{g.playtime}</span>
              </div>
            </div>
          </article>
        ))}
      </div>
      <Link to="/games" className="mt-6 flex items-center justify-center gap-1 text-sm font-bold text-primary-600 hover:text-primary-700 sm:hidden">
        查看全部游戏 <i className="ri-arrow-right-line"></i>
      </Link>
    </section>
  );
}

function HiddenStories() {
  return (
    <section id="hidden" className="relative bg-foreground-950 py-16 md:py-20">
      <div className="mx-auto max-w-6xl px-4 md:px-6">
        <div className="text-center">
          <p className="font-label text-sm font-bold tracking-widest text-accent-400">隐藏剧情专题</p>
          <h2 className="mt-2 font-heading text-2xl font-bold text-background-50 md:text-3xl">被刻意隐藏的故事线</h2>
          <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-foreground-300">
            每一条隐藏剧情都有明确的触发条件。我们把这些机制拆解清楚，帮你找到那些「本以为不存在」的情节。
          </p>
        </div>
        <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2">
          {hiddenStories.map((h, idx) => (
            <article key={h.id} className="rounded-lg border border-foreground-800 bg-foreground-900/40 p-6">
              <div className="flex items-center justify-between">
                <span className="rounded-md bg-accent-500/20 px-2 py-1 font-label text-xs font-bold text-accent-300">{h.tag}</span>
                <span className="font-label text-xs text-foreground-500">#{String(idx + 1).padStart(2, '0')}</span>
              </div>
              <h3 className="mt-3 font-heading text-lg font-bold text-background-50">{h.title}</h3>
              <p className="mt-1 text-xs text-accent-400">{h.game}</p>
              <p className="mt-3 rounded-md bg-foreground-950/60 px-3 py-2 font-label text-xs leading-relaxed text-accent-200">
                <i className="ri-key-2-line mr-1"></i>触发：{h.trigger}
              </p>
              <p className="mt-3 text-sm leading-relaxed text-foreground-300">{h.desc}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function EndingPreview() {
  const list = endingRoutes.slice(0, 3);
  return (
    <section className="mx-auto max-w-6xl px-4 py-16 md:py-20 md:px-6">
      <div className="flex items-end justify-between">
        <div>
          <p className="font-label text-sm font-bold tracking-widest text-accent-600">多结局攻略</p>
          <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">每一个结局，都有一条清晰的路</h2>
        </div>
        <Link to="/endings" className="hidden whitespace-nowrap items-center gap-1 text-sm font-bold text-primary-600 hover:text-primary-700 sm:flex">
          查看全部结局 <i className="ri-arrow-right-line"></i>
        </Link>
      </div>
      <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
        {list.map((e) => (
          <article key={e.id} className="rounded-lg border border-background-200 bg-background-50 p-6">
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
                <i className={`${e.icon} text-xl`}></i>
              </span>
              <div>
                <span className="block font-label text-xs font-bold text-secondary-600">{e.type}</span>
                <span className="block text-sm font-bold text-foreground-950">{e.title}</span>
              </div>
            </div>
            <p className="mt-4 text-xs text-foreground-500">{e.game}</p>
            <p className="mt-2 rounded-md bg-secondary-50 px-3 py-2 font-label text-xs leading-relaxed text-secondary-800">
              条件：{e.cond}
            </p>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">{e.desc}</p>
          </article>
        ))}
      </div>
      <div className="mt-6 text-center">
        <Link to="/endings" className="inline-flex items-center gap-1 text-sm font-bold text-primary-600 hover:text-primary-700 sm:hidden">
          查看全部结局 <i className="ri-arrow-right-line"></i>
        </Link>
      </div>
    </section>
  );
}

function GuideSteps() {
  const steps = [
    { icon: 'ri-search-line', title: '第一周目自然通关', desc: '不看攻略，先体验主线，建立对世界观和角色的完整印象。' },
    { icon: 'ri-list-check-3', title: '对照触发条件', desc: '二周目再按隐藏剧情与结局条件逐项解锁，避免遗漏关键情节。' },
    { icon: 'ri-loop-left-line', title: '利用章节回跳', desc: '多数作品支持回跳关键节点，只在必要时重开周目补数值。' },
    { icon: 'ri-trophy-line', title: '收集全结局', desc: '把真结局、隐藏、普通与坏结局都打一遍，剧情才算完整。' },
  ];
  return (
    <section className="border-y border-background-200 bg-background-100">
      <div className="mx-auto max-w-6xl px-4 py-16 md:px-6">
        <div className="text-center">
          <p className="font-label text-sm font-bold tracking-widest text-accent-600">新手路线</p>
          <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">剧情党的四步玩透法</h2>
        </div>
        <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {steps.map((s, i) => (
            <div key={s.title} className="rounded-lg border border-background-200 bg-background-50 p-6">
              <div className="flex items-center justify-between">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary-100 text-primary-700">
                  <i className={`${s.icon} text-xl`}></i>
                </span>
                <span className="font-heading text-3xl font-bold text-background-300">0{i + 1}</span>
              </div>
              <h3 className="mt-4 text-sm font-bold text-foreground-950">{s.title}</h3>
              <p className="mt-2 text-xs leading-relaxed text-foreground-600">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  return (
    <section className="mx-auto max-w-6xl px-4 py-16 md:py-20 md:px-6">
      <div className="text-center">
        <p className="font-label text-sm font-bold tracking-widest text-accent-600">剧情党口碑</p>
        <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">玩家怎么说</h2>
      </div>
      <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-3">
        {testimonials.map((t) => (
          <figure key={t.id} className="rounded-lg border border-background-200 bg-background-50 p-6">
            <div className="flex items-center gap-1 text-accent-500">
              {[1, 2, 3, 4, 5].map((n) => <i key={n} className="ri-star-fill text-sm"></i>)}
            </div>
            <blockquote className="mt-4 text-sm leading-relaxed text-foreground-700">“{t.text}”</blockquote>
            <figcaption className="mt-4 flex items-center gap-3 border-t border-background-200 pt-4">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary-100 font-heading text-sm font-bold text-secondary-700">
                {t.name.slice(0, 1)}
              </span>
              <span>
                <span className="block text-sm font-bold text-foreground-950">{t.name}</span>
                <span className="block font-label text-xs text-foreground-500">{t.role}</span>
              </span>
            </figcaption>
          </figure>
        ))}
      </div>
    </section>
  );
}

function CtaBanner() {
  return (
    <section className="relative overflow-hidden bg-primary-600 py-14">
      <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-6 px-4 text-center md:flex-row md:px-6 md:text-left">
        <div>
          <h2 className="font-heading text-2xl font-bold text-background-50">想深入某一部作品的故事线？</h2>
          <p className="mt-2 text-sm text-primary-100">从游戏库挑选心仪作品，或直接查看结局攻略，把隐藏内容一网打尽。</p>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row">
          <Link to="/games" className="whitespace-nowrap rounded-md bg-background-50 px-6 py-3 text-sm font-bold text-primary-700 transition-colors hover:bg-background-100">
            进入游戏库
          </Link>
          <Link to="/endings" className="whitespace-nowrap rounded-md border border-background-50/60 px-6 py-3 text-sm font-bold text-background-50 transition-colors hover:bg-background-50/10">
            查看结局攻略
          </Link>
        </div>
      </div>
    </section>
  );
}

export default function HomePage() {
  const faqLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };
  return (
    <>
      <Seo
        title={`火影同人游戏攻略 - 隐藏剧情与多结局指南 | ${siteName}`}
        description={siteDesc}
        jsonLd={[...homeSeo, faqLd]}
      />
      <Navbar />
      <main>
        <Hero />
        <CategoryStrip />
        <FeaturedGames />
        <HiddenStories />
        <EndingPreview />
        <GuideSteps />
        <Testimonials />
        <FaqSection />
        <CtaBanner />
      </main>
      <Footer />
      <ScrollTopButton />
    </>
  );
}